<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
      <div class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Classes & Sessions</h1>
          <p class="text-base text-gray-600 dark:text-gray-400 mt-2">Manage fitness classes, schedules, and instructor assignments</p>
        </div>
        <div class="flex items-center gap-2">
          <input v-model="search" type="text" class="form-input" placeholder="Search classes..." />
          <button class="btn btn-primary" @click="openAdd">Add Class</button>
        </div>
      </div>

      <!-- Class Statistics -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Classes</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-gray-100">{{ classes.length }}</p>
              </div>
              <div class="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Today's Classes</p>
                <p class="text-3xl font-bold text-green-600">{{ todaysClassesCount }}</p>
              </div>
              <div class="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Active Instructors</p>
                <p class="text-3xl font-bold text-purple-600">{{ activeInstructorsCount }}</p>
              </div>
              <div class="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Enrollments</p>
                <p class="text-3xl font-bold text-orange-600">{{ totalEnrollments }}</p>
              </div>
              <div class="w-14 h-14 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Weekly Schedule View -->
      <div class="card dark:bg-gray-900 mb-6">
        <div class="card-header dark:border-gray-800">
          <h2 class="text-xl font-semibold">Weekly Schedule</h2>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-7 gap-2">
            <div v-for="day in weekDays" :key="day" class="text-center">
              <div class="font-medium text-sm mb-2">{{ day }}</div>
              <div class="space-y-2">
                <div v-for="cls in getClassesForDay(day)" :key="cls.id" 
                     class="p-2 bg-blue-100 dark:bg-blue-900 rounded text-xs cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800"
                     @click="viewClass(cls)">
                  <div class="font-medium">{{ cls.name }}</div>
                  <div class="text-gray-600">{{ cls.time }}</div>
                  <div class="text-gray-500">{{ cls.instructor }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card dark:bg-gray-900">
        <div class="card-body">
          <div class="overflow-x-auto">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th class="table-header-cell">Class Name</th>
                  <th class="table-header-cell">Instructor</th>
                  <th class="table-header-cell">Schedule</th>
                  <th class="table-header-cell">Capacity</th>
                  <th class="table-header-cell">Enrolled</th>
                  <th class="table-header-cell">Status</th>
                  <th class="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr class="table-row" v-for="cls in filteredClasses" :key="cls.id">
                  <td class="table-cell">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center mr-3">
                        <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <div>
                        <div class="font-medium">{{ cls.name }}</div>
                        <div class="text-sm text-gray-500">{{ cls.category }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="table-cell">{{ cls.instructor }}</td>
                  <td class="table-cell">
                    <div>{{ cls.day }}</div>
                    <div class="text-sm text-gray-500">{{ cls.time }}</div>
                  </td>
                  <td class="table-cell">{{ cls.capacity }}</td>
                  <td class="table-cell">
                    <div class="flex items-center">
                      <span class="mr-2">{{ cls.enrolled }}</span>
                      <div class="w-20 bg-gray-200 rounded-full h-2">
                        <div class="bg-blue-600 h-2 rounded-full" :style="{ width: (cls.enrolled / cls.capacity * 100) + '%' }"></div>
                      </div>
                    </div>
                  </td>
                  <td class="table-cell">
                    <span :class="getStatusBadgeClass(cls.status)">
                      {{ cls.status }}
                    </span>
                  </td>
                  <td class="table-cell">
                    <div class="flex gap-2">
                      <button class="btn btn-outline btn-sm" @click="edit(cls)">Edit</button>
                      <button class="btn btn-info btn-sm" @click="viewEnrollments(cls)">Enrollments</button>
                      <button class="btn btn-danger btn-sm" @click="remove(cls)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add/Edit Class Modal -->
      <div v-if="open" class="modal-overlay" @click="close">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="text-lg font-semibold">{{ form.id ? 'Edit Class' : 'Add Class' }}</h3>
          </div>
          <div class="modal-body">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="form-label">Class Name</label>
                <input v-model="form.name" type="text" class="form-input" placeholder="Yoga Flow" />
              </div>
              <div>
                <label class="form-label">Category</label>
                <select v-model="form.category" class="form-input">
                  <option value="Yoga">Yoga</option>
                  <option value="Cardio">Cardio</option>
                  <option value="Strength">Strength</option>
                  <option value="Pilates">Pilates</option>
                  <option value="Spinning">Spinning</option>
                  <option value="Zumba">Zumba</option>
                </select>
              </div>
              <div>
                <label class="form-label">Instructor</label>
                <select v-model="form.instructor" class="form-input">
                  <option v-for="instructor in instructors" :key="instructor" :value="instructor">
                    {{ instructor }}
                  </option>
                </select>
              </div>
              <div>
                <label class="form-label">Day</label>
                <select v-model="form.day" class="form-input">
                  <option v-for="day in weekDays" :key="day" :value="day">{{ day }}</option>
                </select>
              </div>
              <div>
                <label class="form-label">Time</label>
                <input v-model="form.time" type="time" class="form-input" />
              </div>
              <div>
                <label class="form-label">Duration (minutes)</label>
                <input v-model="form.duration" type="number" class="form-input" placeholder="60" />
              </div>
              <div>
                <label class="form-label">Capacity</label>
                <input v-model="form.capacity" type="number" class="form-input" placeholder="20" />
              </div>
              <div>
                <label class="form-label">Price</label>
                <input v-model="form.price" type="number" class="form-input" placeholder="15.00" step="0.01" />
              </div>
              <div class="md:col-span-2">
                <label class="form-label">Description</label>
                <textarea v-model="form.description" class="form-input" rows="3" placeholder="Class description..."></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline" @click="close">Cancel</button>
            <button class="btn btn-primary" @click="save">Save</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: () => ({
    search: '',
    open: false,
    form: {
      id: null,
      name: '',
      category: 'Yoga',
      instructor: '',
      day: 'Monday',
      time: '09:00',
      duration: 60,
      capacity: 20,
      price: 15.00,
      description: '',
      enrolled: 0,
      status: 'Active'
    },
    weekDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    instructors: ['Sarah Johnson', 'Mike Chen', 'Emma Davis', 'David Wilson', 'Lisa Brown'],
    classes: [
      { id: 1, name: 'Morning Yoga', category: 'Yoga', instructor: 'Sarah Johnson', day: 'Monday', time: '07:00', duration: 60, capacity: 25, price: 15.00, description: 'Gentle morning yoga flow', enrolled: 18, status: 'Active' },
      { id: 2, name: 'HIIT Cardio', category: 'Cardio', instructor: 'Mike Chen', day: 'Monday', time: '18:00', duration: 45, capacity: 20, price: 18.00, description: 'High-intensity interval training', enrolled: 20, status: 'Active' },
      { id: 3, name: 'Strength Training', category: 'Strength', instructor: 'David Wilson', day: 'Tuesday', time: '17:00', duration: 60, capacity: 15, price: 20.00, description: 'Full body strength workout', enrolled: 12, status: 'Active' },
      { id: 4, name: 'Pilates Core', category: 'Pilates', instructor: 'Emma Davis', day: 'Wednesday', time: '19:00', duration: 45, capacity: 18, price: 16.00, description: 'Core strengthening pilates', enrolled: 15, status: 'Active' },
      { id: 5, name: 'Spinning Class', category: 'Spinning', instructor: 'Lisa Brown', day: 'Thursday', time: '18:30', duration: 60, capacity: 30, price: 22.00, description: 'Indoor cycling workout', enrolled: 28, status: 'Active' }
    ]
  }),
  computed: {
    filteredClasses() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.classes;
      return this.classes.filter(cls =>
        cls.name.toLowerCase().includes(q) ||
        cls.instructor.toLowerCase().includes(q) ||
        cls.category.toLowerCase().includes(q)
      );
    },
    todaysClassesCount() {
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
      return this.classes.filter(cls => cls.day === today).length;
    },
    activeInstructorsCount() {
      return new Set(this.classes.map(cls => cls.instructor)).size;
    },
    totalEnrollments() {
      return this.classes.reduce((sum, cls) => sum + cls.enrolled, 0);
    }
  },
  methods: {
    openAdd() {
      this.form = { id: null, name: '', category: 'Yoga', instructor: this.instructors[0], day: 'Monday', time: '09:00', duration: 60, capacity: 20, price: 15.00, description: '', enrolled: 0, status: 'Active' };
      this.open = true;
    },
    edit(cls) {
      this.form = { ...cls };
      this.open = true;
    },
    remove(cls) {
      this.$bus.$emit('confirm:open', { title: 'Delete Class', message: `Delete ${cls.name}?` });
      this.classes = this.classes.filter(c => c.id !== cls.id);
      this.$bus.$emit('toast:show', { title: 'Class deleted', type: 'warning' });
    },
    viewClass(cls) {
      // In a real app, this would open a detailed view
      this.$bus.$emit('toast:show', { title: `Viewing ${cls.name}`, type: 'info' });
    },
    viewEnrollments(cls) {
      // In a real app, this would open enrollments modal
      this.$bus.$emit('toast:show', { title: `${cls.enrolled} members enrolled`, type: 'info' });
    },
    close() {
      this.open = false;
    },
    save() {
      if (this.form.id) {
        const idx = this.classes.findIndex(c => c.id === this.form.id);
        if (idx > -1) this.classes.splice(idx, 1, { ...this.form });
        this.$bus.$emit('toast:show', { title: 'Class updated', type: 'success' });
      } else {
        this.classes.push({ ...this.form, id: Date.now() });
        this.$bus.$emit('toast:show', { title: 'Class added', type: 'success' });
      }
      this.open = false;
    },
    getClassesForDay(day) {
      return this.classes.filter(cls => cls.day === day);
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'Active':
          return 'badge badge-success';
        case 'Inactive':
          return 'badge badge-danger';
        case 'Full':
          return 'badge badge-warning';
        default:
          return 'badge badge-info';
      }
    }
  }
}
</script>
